import { lightTheme, superBrandGradient } from '@/customTheme'
import ButtonCustom from '@/components/ButtonCustom'
import SlideInAnimation from '@/components/SlideInAnimation'
import { SizeButton } from '@/constants/sizeButton'
import { KIND } from 'baseui/button'
import { useTranslation } from 'react-i18next'
import { setShowModalCustomText } from '@/store/slices/modal/customTextModal'

export type HasFooterActionButtons = 'none' | 'singleButton' | 'twoButtons'

/**
 * Colour treatment for the `singleButton` footer.
 *  - `black`   — the long-standing "Apply all" button: always enabled, black fill.
 *  - `primary` — pink when active, grey/disabled when not (same treatment the
 *                `twoButtons` primary already uses).
 *  - `superGradient` — purple `Upgrade to super` CTA (multi-image generation gate).
 * Defaults to `black` on purpose: the `singleButton` branch has always ignored
 * `isPrimaryButtonActive`, and `InspectorPanel` defaults that flag to `false`, so
 * honouring it unconditionally would ship Resize/Fill's "Apply all" disabled.
 */
export type SingleButtonVariant = 'black' | 'primary' | 'superGradient'

type FooterActionButtonsProps = {
  hasFooterActionButtons: 'singleButton' | 'twoButtons';
  onClickPrimary: () => void;
  onClickSecondary: () => void;
  primaryButtonText?: string;
  isPrimaryButtonActive: boolean;
  isSecondaryButtonActive: boolean;
  onClickSecondaryShowDiscardModal: boolean;
  discardModalHeader?: string;
  discardModalDescription?: string;
  showFooterWithAnimation?: boolean;
  hideFooterWithAnimation?: boolean;
  singleButtonVariant?: SingleButtonVariant;
  /**
   * Opt-in for the `black` variant: greys the button out when
   * `isPrimaryButtonActive` is false. Off by default so the long-standing
   * always-enabled "Apply all" footers keep rendering unchanged.
   */
  honourDisabledOnBlack?: boolean;
  /** Optional line rendered under the single button (e.g. "Generating will use AI credits"). */
  caption?: string;
  /**
   * Rendered above the primary button in the `singleButton` footer — for panels
   * that need a secondary action stacked there (Photoshoot's "Create again").
   */
  aboveButton?: React.ReactNode;
  /** Height of the `singleButton` primary. Defaults to LARGE (48px). */
  singleButtonSize?: SizeButton;
  children?: React.ReactNode;
};

const footerBarStyle: React.CSSProperties = {
  bottom: 0,
  left: 0,
  right: 0,
  height: '80px',
  // Pins the bar: AutoScroll is a shrinkable flex sibling, so without this a
  // taller (auto-height) footer gets compressed and reads as page content.
  flexShrink: 0,
  borderTop: '1px solid #E5E5E5',
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  padding: '16px',
  background: '#fff',
  borderRadius: '0 0 12px 12px',
}

// A caption stacks under the button, so the bar grows instead of holding 80px.
const captionFooterBarStyle: React.CSSProperties = {
  ...footerBarStyle,
  height: 'auto',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '12px',
}

const FooterActionButtons: React.FC<FooterActionButtonsProps> = ({
  hasFooterActionButtons,
  onClickPrimary,
  onClickSecondary,
  primaryButtonText,
  isPrimaryButtonActive,
  isSecondaryButtonActive,
  onClickSecondaryShowDiscardModal,
  discardModalHeader,
  discardModalDescription,
  showFooterWithAnimation = false,
  hideFooterWithAnimation = false,
  singleButtonVariant = 'black',
  honourDisabledOnBlack = false,
  caption,
  aboveButton,
  singleButtonSize = SizeButton.LARGE,
  children,
}) => {

  const { t } = useTranslation()

  if (hasFooterActionButtons === 'singleButton') {
    // 'black' keeps its historic always-enabled behaviour unless a caller opts
    // into the disabled flag; 'primary'/'superGradient' always honour it.
    const isEnabled = singleButtonVariant === 'black'
      ? (honourDisabledOnBlack ? isPrimaryButtonActive : true)
      : isPrimaryButtonActive
    // Disabled is one look for both variants (Figma 12844:34919): grey surface,
    // grey label. Passing `disabled` down as well is what makes ButtonCustom drop
    // its own primary-pink base fill and grey out any start enhancer.
    const background = !isEnabled
      ? lightTheme.colors.grayScale50
      : singleButtonVariant === 'superGradient'
        ? superBrandGradient
        : singleButtonVariant === 'primary'
          ? lightTheme.colors.pink
          : lightTheme.colors.blackGray
    const hoverStyle = !isEnabled
      ? {}
      : singleButtonVariant === 'black'
        ? {
          ':hover': { background: lightTheme.colors.grayScale800 },
          ':active': { background: lightTheme.colors.grayScale700 },
        }
        : {}

    return (
      <SlideInAnimation
        id="footer-action-buttons"
        isVisible
        direction="up"
        showWithAnimation={showFooterWithAnimation}
        hideWithAnimation={hideFooterWithAnimation}
        style={caption || aboveButton ? captionFooterBarStyle : footerBarStyle}
      >
        {aboveButton}
        <ButtonCustom
          type={singleButtonSize}
          kind={KIND.primary}
          disabled={!isEnabled}
          onClick={() => {
            onClickPrimary()
          }}
          style={{
            background,
            pointerEvents: isEnabled ? 'auto' : 'none',
            transition: 'none',
            ...hoverStyle,
          }}
        >
          <span
            style={{
              ...lightTheme.typography.Small14Semibold,
              color: isEnabled ? lightTheme.colors.white : lightTheme.colors.grayScale200,
            }}
          >
            {primaryButtonText ?? t('Apply all')}
          </span>
        </ButtonCustom>
        {caption ? (
          <span
            style={{
              ...lightTheme.typography.Small12regular,
              lineHeight: '20px',
              letterSpacing: '-0.072px',
              color: lightTheme.colors.grayScale600,
              textAlign: 'center',
              // Honours the `\n` in the Super upsell copy.
              whiteSpace: 'pre-wrap',
            }}
          >
            {caption}
          </span>
        ) : null}
        {children}
      </SlideInAnimation>
    )
  }

  return (
    <SlideInAnimation
      id="footer-action-buttons"
      isVisible
      direction="up"
      showWithAnimation={showFooterWithAnimation}
      hideWithAnimation={hideFooterWithAnimation}
      style={footerBarStyle}
    >
      <ButtonCustom
        type={SizeButton.LARGE}
        kind={KIND.secondary}
        onClick={() => {
          if (isPrimaryButtonActive && onClickSecondaryShowDiscardModal) {
            setShowModalCustomText(
              discardModalHeader,
              discardModalDescription,
              t('Discard'),
              t('Cancel'),
              true,
              onClickSecondary,
              () => {}
            )
          }
          else {
            onClickSecondary()
          }
        }}
        style={{
          background: !isSecondaryButtonActive ? '#F2F2F2' : 'auto',
          pointerEvents: !isSecondaryButtonActive ? 'none' : 'auto',
          transition: 'none'
        }}
      >
        <span style={{ ...lightTheme.typography.Small14Semibold, color: !isSecondaryButtonActive ? '#CCCCCC' : '#666666' }}>{t('Cancel')}</span>
      </ButtonCustom>
      <ButtonCustom
        type={SizeButton.LARGE}
        kind={KIND.primary}
        onClick={() => {
          onClickPrimary()
        }}
        style={{
          background: isPrimaryButtonActive ? '#FF0560' : '#F2F2F2',
          pointerEvents: isPrimaryButtonActive ? 'auto' : 'none',
          transition: 'none'
        }}
      >
        <span
          style={{
            ...lightTheme.typography.Small14Semibold,
            color: isPrimaryButtonActive ? '#FFFFFF' : '#CCCCCC',
          }}
        >
          {t('Done')}
        </span>
      </ButtonCustom>
      { children }
    </SlideInAnimation>
  )
}

export default FooterActionButtons
